/**
 * Accept Onboarding Invitation Page (Option B)
 *
 * Customer contacts set their password after receiving an invite email.
 * URL: /onboarding-portal/accept-invite?token=xxx
 */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { onboardingPortalService } from '@/services/api/onboardingPortal';
import { AlertCircle, Lock, Key, UserPlus, ClipboardList } from 'lucide-react';

export default function AcceptOnboardingInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';

  const [token, setToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await onboardingPortalService.acceptInvite(token, password);
      navigate('/onboarding-portal/plans');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to accept invitation.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <span className="ml-3 text-xl font-semibold text-gray-900 dark:text-white">
              Onboarding Portal
            </span>
          </div>
        </div>
      </header>

      {/* Accept Form */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-3">
              <UserPlus className="w-7 h-7 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-2xl">Set Up Your Account</CardTitle>
            <CardDescription>Create a password to access your onboarding plan</CardDescription>
          </CardHeader>

          <form onSubmit={handleAccept}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {!tokenFromUrl && (
                <div className="space-y-2">
                  <Label htmlFor="token">Invitation Token</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="token"
                      value={token}
                      onChange={e => setToken(e.target.value)}
                      placeholder="Paste your invitation token"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    autoFocus={!!tokenFromUrl}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="confirm"
                    type="password"
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-3">
              <Button type="submit" className="w-full" disabled={loading || !token || !password || !confirmPassword}>
                {loading ? 'Setting up...' : 'Create Account & View Plan'}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Already have an account?{' '}
                <a href="/onboarding-portal/login" className="text-blue-600 hover:underline">Sign in</a>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
